import { useCallback, useEffect, useMemo, useState } from "react";
import { useExecuteQuery } from "../../../hooks/usePolicy";
import {
  LinkCell,
  TextCell,
  TimestampCell,
} from "../../PoliciesPageList/CellRenderer/CellRenderer";
import { getSortConfigs, tableColumns } from "./InventoryTableColumnConfig";
import {
  Box,
  Illustration,
  Loader,
  TableSortable,
  Typography,
} from "@saviynt/design-system";
import { FormattedMessage, useIntl } from "react-intl";
import { useInfiniteScroll } from "@repo/mfe-core/host";
import { Resource } from "@repo/api/schemas/policies.schema";
import classNames from "classnames";
import InventorySidePanel from "../InventorySidePanel/InventorySidePanel";
import "./InventoryPageList.css";
import { TableSkeletonLoader, EmptyState } from "@repo/ui";
import useGetUserPermissions, {
  PERMISSION_KEYS,
} from "../../../hooks/useUserPermissions";
import { usePermissionContext } from "../../../context/permissionContext";

export default function InventoryList({
  queryItems,
}: {
  queryItems: ReturnType<typeof useExecuteQuery>;
}) {
  const intl = useIntl();
  const { setIsPermissionEnabled } = usePermissionContext();

  const { permission: assetOwnership } = useGetUserPermissions(
    PERMISSION_KEYS.ISPM_FINDINGS_MANAGE,
  );

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = queryItems;
  const [columns, setColumns] = useState(tableColumns);

  const [selectedRowIndex, setSelectedRowIndex] = useState<
    number | undefined
  >();
  const [isSidepanelOpen, setIsSidepanelOpen] = useState(false);
  const [bridgePosition, setBridgePosition] = useState({
    top: 0,
    left: 0,
    height: 0,
  });

  const tableData = useMemo(() => {
    return data?.pages.flatMap((page) => page.results) || [];
  }, [data]);

  const observerRef = useInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  useEffect(() => {
    setIsPermissionEnabled(assetOwnership?.enabled ?? false);
  }, [assetOwnership, setIsPermissionEnabled]);

  const updateBridgePosition = useCallback((rowIndex: number) => {
    const tableRow = document.querySelector(
      `[data-testid="PoliciesPageList-Table"] tbody tr:nth-child(${rowIndex + 1})`,
    );
    if (tableRow) {
      const rect = tableRow.getBoundingClientRect();
      setBridgePosition({
        top: rect.top + rect.height / 2,
        left: 0,
        height: rect.height,
      });
    }
  }, []);

  const handleRowClick = useCallback(
    (_row: Resource, rowIndex: number) => {
      setSelectedRowIndex(rowIndex);
      setIsSidepanelOpen(true);
      setTimeout(() => updateBridgePosition(rowIndex), 0);
    },
    [updateBridgePosition, tableData],
  );

  const handleColumnSort = useCallback((columnKey: string, order: string) => {
    console.log("Sorting by:", columnKey, "Order:", order.toUpperCase());
  }, []);

  const handleScroll = useCallback(() => {
    if (selectedRowIndex !== undefined) {
      updateBridgePosition(selectedRowIndex);
    }
  }, [selectedRowIndex, updateBridgePosition]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const tableRows = useMemo(
    () =>
      tableData.map((row, index) => ({
        id: row.id,
        name: (
          <div className="policy-name-cell">
            <LinkCell
              text={row.name}
              onClick={() => handleRowClick(row, index)}
            />
          </div>
        ),
        type: <TextCell text={row.type} />,
        created_date: <TimestampCell timestamp={row.created_date} />,
      })),
    [tableData],
  );

  if (isLoading && tableData.length === 0) {
    return <TableSkeletonLoader />;
  }

  if (isError) {
    <Box className="flex flex-col items-center self-center py-16">
      <Illustration kind="Error" />
      <Typography
        kind="body2"
        desktop="body2-d"
        mobile="body2-d"
        color="--color-foreground-danger-medium"
        className="mt-2"
      >
        <FormattedMessage defaultMessage="Something went wrong" id="JqiqNj" />
      </Typography>
    </Box>;
  }

  return (
    <>
      {selectedRowIndex !== undefined && (
        <InventorySidePanel
          bridgePosition={bridgePosition}
          isSidecarOpen={isSidepanelOpen}
          closeSidecar={() => {
            setSelectedRowIndex(undefined);
            setBridgePosition({ top: 0, left: 0, height: 0 });
            setIsSidepanelOpen(false);
          }}
          sidecarMetaData={tableData[selectedRowIndex]}
          variant="inventory"
        />
      )}
      {tableData.length > 0 ? (
        <TableSortable
          columnConfigs={{ isDndDisabled: true }}
          columns={columns}
          setColumns={setColumns}
          data={tableRows}
          sortConfigs={getSortConfigs(intl)}
          onSort={handleColumnSort}
          onRowClick={handleRowClick}
          selectedRowIndex={selectedRowIndex}
          className={classNames("InventoryPageList-Table", {
            "InventoryPageList-Table--shrinked": isSidepanelOpen,
            "InventoryPageList-Table--expanded": !isSidepanelOpen,
          })}
          dataTestId="PoliciesPageList-Table"
        />
      ) : (
        <Box>
          <EmptyState />
        </Box>
      )}
      {isFetchingNextPage && (
        <div data-testid="loader-loop">
          <Loader kind="loop" format="main" color="brand" />
        </div>
      )}
      <div ref={observerRef} style={{ height: "1px" }} />
    </>
  );
}
